/**
 * UTF-8 codec for the data-channel wire.
 *
 * The web transport uses the ambient `TextEncoder`/`TextDecoder`. On React
 * Native those are not engine globals — they arrive as a polyfill imported by
 * `@livekit/react-native`, so they exist at runtime in a correctly-wired app
 * but cannot be relied on at the type level, and are absent entirely in unit
 * tests that never touch the SDK.
 *
 * So: use the platform's codec when it is there (it is faster and battle-
 * tested) and fall back to an explicit implementation otherwise. Chat payloads
 * carry real user text — emoji, Indic scripts, CJK — so a Latin-1 shortcut
 * would corrupt messages rather than merely slow them down.
 */

// `Uint8Array<ArrayBuffer>` rather than the default `ArrayBufferLike`: livekit's
// `publishData` will not accept a possibly-shared buffer.
type Utf8Encoder = { encode(input: string): Uint8Array<ArrayBuffer> };
type Utf8Decoder = { decode(input: Uint8Array): string };

// Reading optional globals off globalThis is the whole point of this module;
// the shapes are checked before use, which is why the casts are safe.
const globalEncoder = (globalThis as { TextEncoder?: new () => Utf8Encoder }).TextEncoder;
const globalDecoder = (globalThis as { TextDecoder?: new () => Utf8Decoder }).TextDecoder;

const CONTINUATION = 0b1000_0000;

const encodeCodePoint = (code: number, out: number[]): void => {
  if (code < 0x80) {
    out.push(code);
    return;
  }
  if (code < 0x800) {
    out.push(0xc0 | (code >> 6), CONTINUATION | (code & 0x3f));
    return;
  }
  if (code < 0x10000) {
    out.push(0xe0 | (code >> 12), CONTINUATION | ((code >> 6) & 0x3f), CONTINUATION | (code & 0x3f));
    return;
  }
  out.push(0xf0 | (code >> 18), CONTINUATION | ((code >> 12) & 0x3f), CONTINUATION | ((code >> 6) & 0x3f), CONTINUATION | (code & 0x3f));
};

/** `for...of` walks code points, so surrogate pairs survive as one character. */
const encodeFallback = (value: string): Uint8Array<ArrayBuffer> => {
  const bytes: number[] = [];
  for (const character of value) {
    encodeCodePoint(character.codePointAt(0) ?? 0, bytes);
  }
  return Uint8Array.from(bytes);
};

/** Byte length of the sequence a lead byte opens; 1 for anything malformed. */
const sequenceLength = (lead: number): number => {
  if (lead < 0x80) return 1;
  if (lead >= 0xf0) return 4;
  if (lead >= 0xe0) return 3;
  if (lead >= 0xc0) return 2;
  return 1;
};

const LEAD_MASKS = [0, 0x7f, 0x1f, 0x0f, 0x07] as const;

const decodeFallback = (bytes: Uint8Array): string => {
  let result = "";
  let index = 0;
  while (index < bytes.length) {
    const lead = bytes[index] ?? 0;
    const length = Math.min(sequenceLength(lead), bytes.length - index);
    let code = lead & (LEAD_MASKS[length] ?? 0x7f);
    for (let offset = 1; offset < length; offset++) {
      code = (code << 6) | ((bytes[index + offset] ?? 0) & 0x3f);
    }
    result += String.fromCodePoint(code);
    index += length;
  }
  return result;
};

export const encodeUtf8 = (value: string): Uint8Array<ArrayBuffer> => (globalEncoder ? new globalEncoder().encode(value) : encodeFallback(value));

export const decodeUtf8 = (bytes: Uint8Array): string => (globalDecoder ? new globalDecoder().decode(bytes) : decodeFallback(bytes));
